// Frontend/src/pages/auth/ClearStoragePage.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearAccessToken } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

export default function ClearStoragePage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useLanguage();
  const [cleared, setCleared] = useState(false);
  const [storageInfo, setStorageInfo] = useState<string[]>([]);

  function checkStorage() {
    const info: string[] = [];
    
    // Check sessionStorage
    info.push(t("clearStorage.sessionStorageHeader"));
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        info.push(`${key}: ${sessionStorage.getItem(key)?.substring(0, 50)}...`);
      }
    }
    
    // Check localStorage
    info.push(`\n${t("clearStorage.localStorageHeader")}`);
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        info.push(`${key}: ${localStorage.getItem(key)?.substring(0, 50)}...`);
      }
    }
    
    setStorageInfo(info);
  }

  async function clearAll() {
    try {
      // First, try to logout via AuthContext (calls backend)
      try {
        await logout();
      } catch (e) {
        console.warn('Logout API call failed, proceeding with local clear:', e);
      }
      
      // Clear in-memory token
      clearAccessToken();
      
      // Clear ALL storage
      sessionStorage.clear();
      localStorage.clear();
      
      // VERIFY storage was actually cleared
      const sessionCleared = sessionStorage.length === 0;
      const localCleared = localStorage.length === 0;
      
      if (!sessionCleared || !localCleared) {
        console.error('Storage clear verification failed!', {
          sessionCleared,
          localCleared,
          sessionLength: sessionStorage.length,
          localLength: localStorage.length
        });
        
        // Fallback: Force manual removal of each key
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key) {
            console.warn('Manually removing sessionStorage key:', key);
            sessionStorage.removeItem(key);
          }
        }
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key) {
            console.warn('Manually removing localStorage key:', key);
            localStorage.removeItem(key);
          }
        }
      }
      
      setCleared(true);
      checkStorage();
      
      // Force a complete reload after 1 second to ensure everything is cleared
      setTimeout(() => {
        window.location.href = "/#/login";
      }, 1000);
    } catch (error) {
      console.error('Error during clear:', error);
      // Force clear anyway
      sessionStorage.clear();
      localStorage.clear();
      clearAccessToken();
      setCleared(true);
      checkStorage();
      
      // Still reload
      setTimeout(() => {
        window.location.href = "/#/login";
      }, 1000);
    }
  }

  function goToLogin() {
    window.location.replace("/#/login");
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-4">{t("clearStorage.title")}</h1>
        
        <div className="space-y-4">
          <button
            onClick={checkStorage}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          >
            {t("clearStorage.checkStorage")}
          </button>
          
          <button
            onClick={clearAll}
            className="w-full bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600"
          >
            {t("clearStorage.clearAll")}
          </button>
          
          {cleared && (
            <button
              onClick={goToLogin}
              className="w-full bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600"
            >
              {t("clearStorage.goToLogin")}
            </button>
          )}
        </div>

        {cleared && (
          <div className="mt-4 p-4 bg-green-100 border border-green-400 rounded">
            <p className="text-green-700 font-semibold">{t("clearStorage.cleared")}</p>
          </div>
        )}

        {storageInfo.length > 0 && (
          <div className="mt-6">
            <h2 className="font-semibold mb-2">{t("clearStorage.contentsTitle")}</h2>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-96">
              {storageInfo.join('\n')}
            </pre>
          </div>
        )}

        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded text-sm">
          <h3 className="font-semibold mb-2">{t("clearStorage.instructionsTitle")}</h3>
          <ol className="list-decimal list-inside space-y-1">
            <li>{t("clearStorage.instructions.check")}</li>
            <li>{t("clearStorage.instructions.clear")}</li>
            <li>{t("clearStorage.instructions.goToLogin")}</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
